/**
 * Trackside dressing: the guardrails, grandstands, start gantry, trees, rocks,
 * advertising hoardings and horizon mountains that turn a road ribbon into a
 * place.
 *
 * Everything that appears more than a handful of times is built as an
 * InstancedMesh with per-instance colour, so a track can carry two hundred props
 * for a couple of draw calls. Builders here are pure: they take positions and
 * colours and hand back an Object3D — track.ts decides where things go.
 *
 * Conventions: props stand on y = 0 and are modelled around their own origin.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Where and how big one instanced prop sits. */
export interface Placement {
  x: number;
  z: number;
  /** Uniform scale applied to the unit prop. */
  scale: number;
  /** Yaw in radians. */
  rotation: number;
}

const _matrix = new THREE.Matrix4();
const _quat = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scale = new THREE.Vector3();
const _color = new THREE.Color();
const _up = new THREE.Vector3(0, 1, 0);

/**
 * Fill an InstancedMesh from placements, tinting each instance with a colour
 * cycled out of `palette` (jittered slightly so no two neighbours match).
 */
function fillInstances(
  mesh: THREE.InstancedMesh,
  placements: Placement[],
  palette: number[],
  rand: () => number,
): void {
  for (let i = 0; i < placements.length; i++) {
    const p = placements[i];
    _quat.setFromAxisAngle(_up, p.rotation);
    _pos.set(p.x, 0, p.z);
    _scale.setScalar(p.scale);
    _matrix.compose(_pos, _quat, _scale);
    mesh.setMatrixAt(i, _matrix);

    _color.setHex(palette[i % palette.length]);
    // A touch of per-instance brightness variation breaks up the repetition.
    _color.multiplyScalar(0.85 + rand() * 0.3);
    mesh.setColorAt(i, _color);
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.computeBoundingSphere();
}

/**
 * How a themed prop glows. Instance colours multiply the *diffuse* term only, so
 * emissive has to carry its own colour — on the night circuit that is what makes
 * the trackside furniture read as backlit signage.
 */
export interface Glow {
  color: number;
  intensity: number;
}

const NO_GLOW: Glow = { color: 0x000000, intensity: 0 };

/** Standard opaque prop material; instance colours multiply into it. */
function propMaterial(roughness = 0.9, metalness = 0.0, glow: Glow = NO_GLOW): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness,
    metalness,
    emissive: new THREE.Color(glow.color),
    emissiveIntensity: glow.intensity,
  });
}

// ---------------------------------------------------------------------------
// Trees
// ---------------------------------------------------------------------------

/**
 * Conifers: a tapered trunk under three stacked, slightly offset cones. Returns
 * two instanced meshes (trunks and canopies) so the wood and the needles can be
 * coloured independently.
 */
export function createConifers(
  placements: Placement[],
  trunkColor: number,
  foliage: number[],
  glow: Glow,
  rand: () => number,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'conifers';
  if (placements.length === 0) return group;

  const trunkGeo = new THREE.CylinderGeometry(0.42, 0.75, 4.4, 7);
  trunkGeo.translate(0, 2.2, 0);
  const trunks = new THREE.InstancedMesh(trunkGeo, propMaterial(1), placements.length);
  fillInstances(trunks, placements, [trunkColor], rand);

  // Three cones of shrinking radius stacked into one canopy geometry.
  const tiers: THREE.BufferGeometry[] = [];
  const shape = [
    { r: 3.4, h: 5.2, y: 5.0 },
    { r: 2.6, h: 4.6, y: 7.8 },
    { r: 1.7, h: 4.0, y: 10.4 },
  ];
  for (const t of shape) {
    const cone = new THREE.ConeGeometry(t.r, t.h, 9);
    cone.translate(0, t.y, 0);
    tiers.push(cone);
  }
  const canopyGeo = mergeGeometries(tiers, false) ?? tiers[0];
  tiers.forEach((t) => t !== canopyGeo && t.dispose());

  const canopies = new THREE.InstancedMesh(canopyGeo, propMaterial(0.95, 0, glow), placements.length);
  fillInstances(canopies, placements, foliage, rand);

  group.add(trunks, canopies);
  return group;
}

/** Broadleaf trees: a short trunk under a cluster of three overlapping blobs. */
export function createBroadleaves(
  placements: Placement[],
  trunkColor: number,
  foliage: number[],
  glow: Glow,
  rand: () => number,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'broadleaves';
  if (placements.length === 0) return group;

  const trunkGeo = new THREE.CylinderGeometry(0.5, 0.85, 4.0, 7);
  trunkGeo.translate(0, 2.0, 0);
  const trunks = new THREE.InstancedMesh(trunkGeo, propMaterial(1), placements.length);
  fillInstances(trunks, placements, [trunkColor], rand);

  const blobs: THREE.BufferGeometry[] = [];
  const cluster = [
    { r: 3.0, x: 0, y: 6.2, z: 0 },
    { r: 2.2, x: 2.0, y: 5.4, z: 0.8 },
    { r: 2.0, x: -1.7, y: 5.6, z: -1.1 },
  ];
  for (const b of cluster) {
    const blob = new THREE.IcosahedronGeometry(b.r, 1);
    blob.scale(1, 0.85, 1);
    blob.translate(b.x, b.y, b.z);
    blobs.push(blob);
  }
  const canopyGeo = mergeGeometries(blobs, false) ?? blobs[0];
  blobs.forEach((b) => b !== canopyGeo && b.dispose());

  const canopies = new THREE.InstancedMesh(canopyGeo, propMaterial(0.95, 0, glow), placements.length);
  fillInstances(canopies, placements, foliage, rand);

  group.add(trunks, canopies);
  return group;
}

// ---------------------------------------------------------------------------
// Rocks
// ---------------------------------------------------------------------------

/**
 * Boulders: a low-poly icosahedron with its vertices pushed around so the facets
 * read as fractured stone. Per-instance rotation and scale do the rest.
 */
export function createRocks(
  placements: Placement[],
  palette: number[],
  rand: () => number,
): THREE.Mesh {
  const geo = new THREE.IcosahedronGeometry(1, 1);
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const jitter = 0.72 + rand() * 0.56;
    pos.setXYZ(i, pos.getX(i) * jitter, pos.getY(i) * jitter * 0.75, pos.getZ(i) * jitter);
  }
  geo.computeVertexNormals();
  // Sink the boulder slightly so it looks bedded into the ground.
  geo.translate(0, 0.55, 0);

  const mesh = new THREE.InstancedMesh(geo, propMaterial(1), Math.max(placements.length, 1));
  fillInstances(mesh, placements, palette, rand);
  mesh.count = placements.length;
  mesh.name = 'rocks';
  return mesh;
}

// ---------------------------------------------------------------------------
// Advertising hoardings
// ---------------------------------------------------------------------------

/**
 * Sponsor boards standing behind the barriers. Flat coloured panels — from the
 * cockpit they read as the blur of trackside advertising.
 */
export function createHoardings(
  placements: Placement[],
  palette: number[],
  glow: Glow,
  rand: () => number,
): THREE.Mesh {
  const geo = new THREE.BoxGeometry(7, 2.0, 0.25);
  geo.translate(0, 1.5, 0);
  const mesh = new THREE.InstancedMesh(
    geo,
    propMaterial(0.6, 0.1, glow),
    Math.max(placements.length, 1),
  );
  fillInstances(mesh, placements, palette, rand);
  mesh.count = placements.length;
  mesh.castShadow = false; // flat panels, and it keeps the shadow pass cheap
  mesh.name = 'hoardings';
  return mesh;
}

// ---------------------------------------------------------------------------
// Guardrail
// ---------------------------------------------------------------------------

/**
 * Armco barrier following `path` (an already-offset closed polyline): a metal
 * ribbon on posts. Built as one double-sided strip plus one instanced post mesh,
 * so a full lap of barrier costs two draw calls.
 */
export function createGuardrail(
  path: THREE.Vector3[],
  railColor: number,
  postColor: number,
  glow: Glow,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'guardrail';
  const n = path.length;
  if (n < 3) return group;

  const RAIL_BOTTOM = 0.55;
  const RAIL_TOP = 1.15;

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  let run = 0;
  for (let i = 0; i < n; i++) {
    const p = path[i];
    const prev = path[(i - 1 + n) % n];
    const next = path[(i + 1) % n];
    // Outward normal from the local tangent, used to face the ribbon.
    const tx = next.x - prev.x;
    const tz = next.z - prev.z;
    const tl = Math.hypot(tx, tz) || 1;
    normals.push(tz / tl, 0, -tx / tl, tz / tl, 0, -tx / tl);

    positions.push(p.x, RAIL_BOTTOM, p.z);
    positions.push(p.x, RAIL_TOP, p.z);
    uvs.push(run / 6, 0, run / 6, 1);
    run += Math.hypot(next.x - p.x, next.z - p.z);
  }
  for (let i = 0; i < n; i++) {
    const a = i * 2;
    const b = i * 2 + 1;
    const c = ((i + 1) % n) * 2;
    const d = ((i + 1) % n) * 2 + 1;
    indices.push(a, c, b, b, c, d);
  }

  const railGeo = new THREE.BufferGeometry();
  railGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  railGeo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  railGeo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  railGeo.setIndex(indices);
  railGeo.computeBoundingSphere();

  const rail = new THREE.Mesh(
    railGeo,
    new THREE.MeshStandardMaterial({
      color: railColor,
      roughness: 0.42,
      metalness: 0.75,
      side: THREE.DoubleSide,
      emissive: new THREE.Color(glow.color),
      emissiveIntensity: glow.intensity,
    }),
  );
  rail.castShadow = true;
  rail.receiveShadow = true;
  group.add(rail);

  // Posts every few metres, dropped straight onto the path.
  const every = Math.max(1, Math.round(7 / Math.max(1, run / n)));
  const postCount = Math.floor(n / every);
  const postGeo = new THREE.BoxGeometry(0.22, RAIL_TOP, 0.22);
  postGeo.translate(0, RAIL_TOP / 2, 0);
  const posts = new THREE.InstancedMesh(
    postGeo,
    new THREE.MeshStandardMaterial({ color: postColor, roughness: 0.6, metalness: 0.5 }),
    Math.max(postCount, 1),
  );
  for (let i = 0; i < postCount; i++) {
    const p = path[(i * every) % n];
    _matrix.makeTranslation(p.x, 0, p.z);
    posts.setMatrixAt(i, _matrix);
  }
  posts.count = postCount;
  posts.instanceMatrix.needsUpdate = true;
  posts.castShadow = true;
  posts.computeBoundingSphere();
  group.add(posts);

  return group;
}

// ---------------------------------------------------------------------------
// Start / finish structures
// ---------------------------------------------------------------------------

/**
 * The gantry straddling the start line: two pillars, a deep beam, a lit banner
 * and a row of start lights. `width` is the road width it has to clear.
 */
export function createStartGantry(width: number, accent: number, beamColor: number): THREE.Group {
  const gantry = new THREE.Group();
  gantry.name = 'gantry';

  const span = width + 6;
  const height = 8.5;

  const structure = new THREE.MeshStandardMaterial({ color: beamColor, roughness: 0.5, metalness: 0.55 });
  const banner = new THREE.MeshStandardMaterial({
    color: accent,
    roughness: 0.55,
    metalness: 0.1,
    emissive: new THREE.Color(accent),
    emissiveIntensity: 0.35,
  });

  for (const side of [-1, 1]) {
    const pillar = new THREE.Mesh(new THREE.BoxGeometry(1.1, height, 1.1), structure);
    pillar.position.set((span / 2) * side, height / 2, 0);
    pillar.castShadow = true;
    gantry.add(pillar);

    // Diagonal brace back to the beam.
    const brace = new THREE.Mesh(new THREE.BoxGeometry(0.4, 3.4, 0.4), structure);
    brace.position.set((span / 2 - 1.1) * side, height - 1.6, 0);
    brace.rotation.z = (Math.PI / 4) * side;
    gantry.add(brace);
  }

  const beam = new THREE.Mesh(new THREE.BoxGeometry(span + 1.4, 1.5, 1.4), structure);
  beam.position.set(0, height, 0);
  beam.castShadow = true;
  gantry.add(beam);

  const board = new THREE.Mesh(new THREE.BoxGeometry(span * 0.72, 2.1, 0.4), banner);
  board.position.set(0, height + 1.7, 0);
  board.castShadow = true;
  gantry.add(board);

  // Five start lights hanging under the beam.
  const bulbGeo = new THREE.SphereGeometry(0.34, 12, 10);
  const bulbMat = new THREE.MeshStandardMaterial({
    color: 0x2a0a0a,
    roughness: 0.3,
    emissive: new THREE.Color(0xff2a1a),
    emissiveIntensity: 1.6,
  });
  for (let i = 0; i < 5; i++) {
    const bulb = new THREE.Mesh(bulbGeo, bulbMat);
    bulb.position.set((i - 2) * 2.1, height - 1.15, 0);
    gantry.add(bulb);
  }

  return gantry;
}

/**
 * A grandstand: three tiers of seating on a raked base, a cantilevered roof, and
 * a crowd of instanced specks so it never looks empty. Faces -Z (toward the
 * track); the caller rotates it into place.
 */
export function createGrandstand(structureColor: number, roofColor: number, rand: () => number): THREE.Group {
  const stand = new THREE.Group();
  stand.name = 'grandstand';

  const WIDTH = 26;
  const structure = new THREE.MeshStandardMaterial({ color: structureColor, roughness: 0.85 });
  const roofMat = new THREE.MeshStandardMaterial({ color: roofColor, roughness: 0.5, metalness: 0.4 });

  const tiers = 4;
  for (let i = 0; i < tiers; i++) {
    const depth = 2.4;
    const step = new THREE.Mesh(new THREE.BoxGeometry(WIDTH, 1.5 + i * 1.5, depth), structure);
    step.position.set(0, (1.5 + i * 1.5) / 2, i * depth);
    step.castShadow = true;
    step.receiveShadow = true;
    stand.add(step);
  }

  // Roof on two rear columns, tilted forward over the seating.
  const roof = new THREE.Mesh(new THREE.BoxGeometry(WIDTH + 2, 0.4, 11), roofMat);
  roof.position.set(0, 10.4, 4.4);
  roof.rotation.x = -0.10;
  roof.castShadow = true;
  stand.add(roof);
  for (const side of [-1, 1]) {
    const column = new THREE.Mesh(new THREE.BoxGeometry(0.7, 10.4, 0.7), roofMat);
    column.position.set(side * (WIDTH / 2 - 0.6), 5.2, 9.2);
    column.castShadow = true;
    stand.add(column);
  }

  // The crowd: small bright blocks scattered across the tiers.
  const CROWD = 150;
  const crowdGeo = new THREE.BoxGeometry(0.5, 0.7, 0.4);
  const crowd = new THREE.InstancedMesh(crowdGeo, propMaterial(1), CROWD);
  const shirt = [0xf2f2f2, 0xe23b3b, 0x2f6fed, 0xffd400, 0x24b06a, 0xff8c42, 0xb14bff];
  for (let i = 0; i < CROWD; i++) {
    const tier = Math.floor(rand() * tiers);
    _pos.set(
      (rand() - 0.5) * (WIDTH - 2),
      1.5 + tier * 1.5 + 0.35,
      tier * 2.4 + (rand() - 0.5) * 1.2,
    );
    _quat.setFromAxisAngle(_up, (rand() - 0.5) * 0.5);
    _scale.setScalar(0.85 + rand() * 0.35);
    _matrix.compose(_pos, _quat, _scale);
    crowd.setMatrixAt(i, _matrix);
    _color.setHex(shirt[Math.floor(rand() * shirt.length)]);
    crowd.setColorAt(i, _color);
  }
  crowd.instanceMatrix.needsUpdate = true;
  if (crowd.instanceColor) crowd.instanceColor.needsUpdate = true;
  crowd.castShadow = true;
  crowd.computeBoundingSphere();
  stand.add(crowd);

  return stand;
}

// ---------------------------------------------------------------------------
// Horizon
// ---------------------------------------------------------------------------

/**
 * A ring of large cones far outside the track. They sit deep in the fog, so they
 * read purely as silhouette — but without them the horizon is a flat line and
 * the world feels like a tabletop.
 */
export function createMountainRing(
  radius: number,
  color: number,
  count: number,
  rand: () => number,
): THREE.Mesh {
  const geo = new THREE.ConeGeometry(1, 1, 6, 1);
  geo.translate(0, 0.5, 0);
  const mesh = new THREE.InstancedMesh(
    geo,
    new THREE.MeshStandardMaterial({ color, roughness: 1, flatShading: true }),
    count,
  );

  for (let i = 0; i < count; i++) {
    // Even spread with jitter, so the ring never looks like a clock face.
    const angle = (i / count) * Math.PI * 2 + (rand() - 0.5) * 0.22;
    const dist = radius * (0.85 + rand() * 0.4);
    const height = 90 + rand() * 170;
    const spread = height * (0.75 + rand() * 0.5);
    _pos.set(Math.cos(angle) * dist, -6, Math.sin(angle) * dist);
    _quat.setFromAxisAngle(_up, rand() * Math.PI);
    _scale.set(spread, height, spread);
    _matrix.compose(_pos, _quat, _scale);
    mesh.setMatrixAt(i, _matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.computeBoundingSphere();
  mesh.name = 'mountains';
  return mesh;
}
